import type { Scene } from '@src/editor/models/layout';
import type {
  ISceneFramingService,
  SmartFramingService,
} from '@src/services/framing/SmartFramingService';
import type { FaceDetection } from '@src/services/framing/types';

type SceneFramer = Pick<
  SmartFramingService,
  'autoFrameSingleScene' | 'autoFrameSingleSceneWithSpeakerContext'
>;

/**
 * The framing service handed to commands that create scenes mid-edit
 * (transcript restore, trim-extend). Frames each new scene on its active
 * speaker when a speaker→face map is available — the same selection as
 * "Auto-Frame All Scenes (Speaker-Face Matching)" — and falls back to
 * face-only framing otherwise.
 *
 * The map is read through `getSpeakerMap` on every call, so a map that loads
 * or changes after construction is picked up.
 */
export class SpeakerAwareSceneFramer implements ISceneFramingService {
  constructor(
    private readonly framer: SceneFramer,
    private readonly getSpeakerMap: () => Record<string, string[]> | null,
  ) {}

  autoFrameSingleScene(
    sceneInfo: { scene: Scene; startTime: number; endTime: number },
    faceDetections: FaceDetection[],
    targetAspectRatio?: number,
  ): boolean {
    const speakerMap = this.getSpeakerMap();
    if (speakerMap && Object.keys(speakerMap).length > 0) {
      return this.framer.autoFrameSingleSceneWithSpeakerContext(
        sceneInfo,
        faceDetections,
        speakerMap,
        targetAspectRatio,
      );
    }
    return this.framer.autoFrameSingleScene(
      sceneInfo,
      faceDetections,
      targetAspectRatio,
    );
  }
}
